import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { Button } from './shared/Button'
import { startCheckout } from '../../lib/stripe/startCheckout'
import { useI18n } from '../../i18n/I18nProvider'

type PlanId = 'community' | 'matching'

const plans: Array<{
  id: PlanId
  highlighted?: boolean
  featureKeys: string[]
}> = [
  {
    id: 'community',
    featureKeys: [
      'payment.plan.community.f1',
      'payment.plan.community.f2',
      'payment.plan.community.f3',
      'payment.plan.community.f4',
    ],
  },
  {
    id: 'matching',
    highlighted: true,
    featureKeys: [
      'payment.plan.matching.f1',
      'payment.plan.matching.f2',
      'payment.plan.matching.f3',
      'payment.plan.matching.f4',
    ],
  },
]

export function PaymentDecisionStep({ email }: { email: string }) {
  const { t } = useI18n()
  const [loading, setLoading] = useState<null | 'basic' | 'active'>(null)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const handleContinue = async (planId: PlanId) => {
    // Map your UI plan ids -> backend plan keys
    const plan: 'basic' | 'active' = planId === 'matching' ? 'active' : 'basic'
    if (!email) {
      setError(t('payment.error.missingEmail'))
      return
    }
    setError(null)
    setLoading(plan)
    localStorage.setItem('niyyah_plan', plan)
    try {
      await startCheckout({
        email,
        plan,
      })
      // startCheckout redirects, so nothing after this usually runs
    } catch (e: any) {
      setError(e?.message || t('payment.error.failed'))
      setLoading(null)
    }
  }

  return (
    <div className="flex flex-col items-center min-h-[70vh] text-center space-y-16 py-12 relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        className="space-y-6 max-w-2xl relative z-10"
      >
        <h1 className="text-4xl sm:text-5xl font-normal text-[#2D2A35] tracking-tight leading-tight">
          {t('payment.title')}
        </h1>

        <p className="text-xl text-[#6B6575] leading-relaxed font-light">
          {t('payment.subtitle')}
        </p>

        {/* Trial box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.2,
            duration: 0.8,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          className="inline-flex flex-col items-center gap-1.5 px-6 py-3.5 rounded-2xl bg-white border border-[#E8E4EE] shadow-sm"
        >
          <span className="text-sm font-medium text-[#2D2A35] tracking-wide">
            {t('payment.trial.title')}
          </span>
          <span className="text-xs text-[#9B95A3] font-light">
            {t('payment.trial.subtitle')}
          </span>
        </motion.div>

        {error && (
          <div className="space-y-3">
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-2xl px-4 py-3">
              {error}
            </div>
            <button
              type="button"
              onClick={() => navigate('/intake', { replace: true })}
              className="text-sm text-[#9B87C7] hover:text-[#7D6BA8] transition-colors"
            >
              {t('payment.backToIntake')}
            </button>
          </div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.3,
          duration: 1.2,
          ease: [0.25, 0.1, 0.25, 1],
        }}
        className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-10 px-4 relative z-10"
      >
        {plans.map((plan, index) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.4 + index * 0.2,
              duration: 1,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            whileHover={{
              y: -5,
              transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
            }}
            className={`
              relative bg-white rounded-3xl p-12 border transition-all duration-500 overflow-hidden group text-left
              ${
                plan.highlighted
                  ? 'border-[#9B87C7] shadow-2xl shadow-[#9B87C7]/10'
                  : 'border-[#E8E4EE] shadow-lg hover:shadow-xl hover:border-[#B5A8D4]'
              }
            `}
          >
            {plan.highlighted && (
              <div className="absolute top-8 right-8">
                <span className="gradient-button tactile-texture text-white text-xs font-bold px-5 py-2 rounded-full shadow-sm tracking-widest uppercase">
                  {t('payment.mostPopular')}
                </span>
              </div>
            )}

            <div className="space-y-10 relative z-10">
              <div className="space-y-4">
                <h3 className="text-2xl font-medium text-[#2D2A35]">
                  {t(`payment.plan.${plan.id}.title`)}
                </h3>
                <div className="flex items-baseline gap-3">
                  <span className="text-6xl font-normal text-[#2D2A35] tracking-tight">
                    {t(`payment.plan.${plan.id}.price`)}
                  </span>
                  <span className="text-[#9B95A3] text-lg font-light">
                    {t(`payment.plan.${plan.id}.period`)}
                  </span>
                </div>
              </div>

              <div className="h-px w-full bg-[#E8E4EE]" />

              <ul className="space-y-6">
                {plan.featureKeys.map((k) => (
                  <li key={k} className="flex items-start gap-5">
                    <div className="w-6 h-6 rounded-full bg-[#F8F8FA] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#9B87C7] stroke-[2]" />
                    </div>
                    <span className="text-[#6B6575] text-lg leading-relaxed font-light">
                      {t(k)}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="pt-6">
                <Button
                  onClick={() => handleContinue(plan.id)}
                  variant={plan.highlighted ? 'primary' : 'secondary'}
                  fullWidth
                  className="py-5 text-lg"
                  disabled={loading !== null}
                >
                  {loading ? t('payment.redirecting') : t(`payment.plan.${plan.id}.cta`)}
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: 0.8,
          duration: 1.2,
          ease: [0.25, 0.1, 0.25, 1],
        }}
        className="space-y-8 max-w-3xl pt-12"
      >
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-base text-[#9B95A3] font-medium">
          <span className="flex items-center gap-4 px-6 py-3 rounded-full bg-white border border-[#E8E4EE] shadow-sm">
            <div className="w-2 h-2 bg-[#9B87C7] rounded-full" />
            {t('payment.badge.private')}
          </span>
          <span className="flex items-center gap-4 px-6 py-3 rounded-full bg-white border border-[#E8E4EE] shadow-sm">
            <div className="w-2 h-2 bg-[#9B87C7] rounded-full" />
            {t('payment.badge.flexible')}
          </span>
        </div>

        <p className="text-sm text-[#B5A8C3] font-light tracking-wide uppercase opacity-80">
          {t('payment.footer')}
        </p>
      </motion.div>
    </div>
  )
}